import { recordActivity } from '../../../services/engagement';
import React, { useState, useMemo, useEffect } from "react";
import { TbCopy, TbTrash, TbPencil, TbCpu, TbBolt, TbShieldLock, TbPlugConnected, TbCheck, TbAlertTriangle,
         TbArrowBackUp, TbArrowLeft, TbArrowRight, TbPlayerPlay, TbDeviceDesktop, TbDeviceMobile, TbSparkles } from "react-icons/tb";
import { useLeadMagnets, useLeads } from "../../hooks/useLeadMagnets";
import { useTenant } from "../../hooks/useTenant";
import { useLeadDestinations } from "../../hooks/useLeadDestinations";
import { useSort } from "../../hooks/useSort";
import { fmtDateTime } from "../../lib/dates";
import { Button, Field, Alert, EmptyState, SortHeader, Toggle, SettingRow, ConfirmModal, Swatches,
         SkeletonRows } from "../../components";
import { THEME_OPTIONS, themeVars, contrastCheck, themeDefaults } from "../../lib/widgetThemes";
import { LEAD_MAGNET_TYPES, DEFAULT_TYPE, leadMagnetType } from "../../lib/leadMagnetTypes";
import { useNavIntent } from "../../shell/intent";
import "./Scanner.css";
import appearanceTools from "../../lib/widgetAppearance";
import { widgetResizeScript, widgetFrameStyle } from "../../lib/widgetEmbed";
import { ColorField, ScreenColorPicker, PaletteSwatches, WebsiteColorsProvider, WebsitePalette, WebsiteReferencePreview } from "./WidgetColorTools";

// Attack surface lead magnet. Two views because they are two jobs: building the embed,
// and working the contacts it produced.
//
// The builder is config on the left and a live preview on the right. The preview is
// the specification — a separate static mock would drift from what the widget renders
// the first time a field changes.

const ACCENTS = ["#1f7fe5", "#093070", "#26814f", "#b83b4c", "#000000"];
const CONFIG_TABS = ["Basic", "Layout", "Form", "Display", "Widget colors", "Report branding"];

// Building a new widget runs as a wizard; editing an existing one opens the tab panel
// above. Both drive the same `cfg` and the same panels — a step simply decides which
// panel is on screen, so there is one implementation of every control.
//
// The tabs are a reference layout: six of them, forty-odd controls, no order, and no
// indication that picking the lead magnet first is what makes the rest coherent. That is
// fine for changing one setting on a widget you already understand and hostile as a first
// run. The steps are ordered the way the decisions actually depend on each other: what you
// are offering, what it says, what it looks like, what happens when someone submits it.
//
// "Report branding" and "Widget colors" are deliberately not steps. They inherit sensible
// values from the workspace, nobody needs them to ship a working widget, and the review
// step links into the full panel for anyone who does.
const WIZARD_STEPS = [
  {
    key: "Basic", label: "Offer",
    title: "Pick the lead magnet you want to offer",
    note: "The report decides which checks run and what the widget promises. Everything after this follows from it.",
  },
  {
    key: "Layout", label: "Look",
    title: "How should it look, and what should it say?",
    note: "Shape, palette and wording together — they are one judgement, and the preview beside you shows the result of each change.",
  },
  {
    key: "Form", label: "Form",
    title: "The form itself",
    note: "What the button says, and what the box asks for.",
  },
  {
    key: "Display", label: "Results",
    title: "What happens after they scan",
    note: "Where the visitor's report appears, and what you collect along the way.",
  },
  {
    key: "review", label: "Publish",
    title: "Ready to publish",
    note: "Check the preview, then generate the embed code for your page.",
  },
];
const REVIEW_STEP = WIZARD_STEPS.length - 1;

// The tenant columns the report renders with — cover byline, contact block, tagline,
// layout and palette. A widget may override any of them; see BRANDING_NOTE below.
const BRANDING_TEXT_FIELDS = [
  ["tenant_name", "Workspace name", ""],
  ["website", "Website", "example.com"],
  ["address", "Address", ""],
  ["contact_email", "Contact email", ""],
  ["contact_phone", "Contact phone", ""],
  ["report_tagline", "Report tagline", ""],
];
const HEX = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

// Straight from v1's LAYOUTS and THEMES constants. Both are pickers rather than selects
// there, because the choice is visual — which is also why the cards carry a preview.

// Derived from the widget's own palette (lib/widgetThemes.js) rather than restated here,
// so a swatch cannot disagree with the preview beside it.
const THEMES = THEME_OPTIONS.map((t) => ({ ...t, ink: t.text }));

// What the Full layout shows above the form. Both lists are the widget's, not invented
// for the preview.
const STAT_CHIPS = ["Dark Web", "SSL/TLS", "Headers", "DNS", "Email"];
const TRUST_BADGES = ["100% Secure", "Instant Results", "No Credit Card"];


// Each config group names itself, the way the v1 creator does. A tab word on its own
// does not always tell anyone what is behind it.
const TabHead = ({ title, note }) => (
  <div className="v2-tabhead">
    <div className="v2-tabhead-title">{title}</div>
    <div className="v2-tabhead-note">{note}</div>
  </div>
);

// A colour as both a picker and a hex field, because people arrive with a brand hex to
// paste as often as they arrive wanting to browse. `swatches` adds the product palette
// in front for the fields that have a sensible default set.
//
// An empty value means "inherit the theme", so the picker shows a neutral rather than
// claiming a colour the server was never sent, and Clear puts it back.
// `fallback` is what this colour currently resolves to when the field is left blank —
// the theme's own value. The picker opens on it and the hex box shows it as a
// placeholder, so the control says what the widget is actually painting rather than
// showing an unrelated grey and an empty box.
// Native details preserve field values while collapsed and support keyboard toggling.
const SettingsGroup = ({ title, description, defaultOpen = false, tourId, children }) => (
  <details className="v2-settings-group" data-tour={tourId || title} open={defaultOpen || undefined}>
    <summary><span>{title}</span>{description && <small>{description}</small>}</summary>
    <div className="v2-settings-group-body">{children}</div>
  </details>
);

// Three jobs, three views: build one, look after the ones that exist, work the leads they
// produced. The saved-widgets table used to sit under the builder, which made an already
// long screen longer and put a management table in the path of someone halfway through
// creating something.
//
// The widget list is loaded here rather than in the builder so the tab can carry its count
// — the same way Leads does — and so both views read one copy of it.
// Two views: build one, and work the leads the built ones produced. The list of saved
// widgets moved out to its own screen (SavedWidgetsScreen) — looking after what exists is
// a different job from making another, and it was reachable only by opening the builder.
//
// `routeParam` is a widget key: /scanner/lead-magnet/<key> opens the builder on that
// widget, which is how Edit works from the saved-widgets screen and what makes a
// half-finished widget a link someone can come back to.
const LeadMagnetScreen = ({ me, routeParam, onNavigate }) => {
  const [view, setView] = useState("builder");
  const { leads } = useLeads();
  const magnets = useLeadMagnets();

  // From the rail flyout. "New" and "edit" belong to WidgetBuilder, so the view is
  // switched here and the request handed down for it to act on once the list has loaded.
  const [pendingIntent, setPendingIntent] = useState(null);
  useNavIntent((intent) => {
    if (intent.type === "list") { onNavigate?.("widgets"); return; }
    setView("builder");
    setPendingIntent(intent);
  });

  return (
    <div className="v2-fullscreen v2-lead-magnet">
      <div className="v2-studio-header">
        <div className="v2-studio-heading"><span className="v2-studio-eyebrow">Lead generation</span><h1>Lead magnet studio</h1><p>Make your next lead magnet feel at home on your website.</p></div>
      {view === 'builder' && <Button style={{ marginLeft: 'auto' }} onClick={() => window.dispatchEvent(new Event('v2:page-tour'))}>Walk me through</Button>}
      <div className="v2-views">
        <button type="button" className={`v2-view${view === "builder" ? " on" : ""}`} onClick={() => setView("builder")}>
          Widget builder
        </button>
        <button type="button" className={`v2-view${view === "leads" ? " on" : ""}`} onClick={() => setView("leads")}>
          Leads<span className="n">{leads.length}</span>
        </button>
      </div>
      </div>
      {view === "leads"
        ? <LeadsView leads={leads} />
        : <WidgetBuilder me={me} magnets={magnets} editKey={routeParam} onNavigate={onNavigate}
                         pendingIntent={pendingIntent} onIntentHandled={() => setPendingIntent(null)} />}
    </div>
  );
};

const WidgetBuilder = ({ me, magnets, editKey, onNavigate, pendingIntent, onIntentHandled }) => {
  const { widgets, loading, error, create, update, remove } = magnets;
  const [tab, setTab] = useState("Basic");
  const [tourFocus, setTourFocus] = useState(null);
  useEffect(() => {
    const onTour = event => {
      const detail = event.detail || {};
      if (CONFIG_TABS.includes(detail.panel)) setTab(detail.panel);
      if (Number.isInteger(detail.step) && detail.step >= 0 && detail.step < WIZARD_STEPS.length) setStep(detail.step);
      setTourFocus(detail);
    };
    window.addEventListener('v2:widget-tour', onTour);
    return () => window.removeEventListener('v2:widget-tour', onTour);
  }, []);
  // Wizard position. `wizard` below turns it off once an existing widget is loaded for
  // editing, and the last step can hand over to the tab panel for the settings the steps
  // leave out.
  const [step, setStep] = useState(0);
  useEffect(() => {
    if (!tourFocus) return;
    const root = document.querySelector('.v2-lead-magnet');
    root?.querySelectorAll('details[data-tour]').forEach(group => {
      const id = group.getAttribute('data-tour');
      if (tourFocus.restore && Object.prototype.hasOwnProperty.call(tourFocus.restore, id)) group.open = tourFocus.restore[id];
      else if (id === tourFocus.group) group.open = true;
    });
    if (typeof tourFocus.scroll === 'number') {
      const body = root?.querySelector('.v2-split-body');
      if (body) body.scrollTop = tourFocus.scroll;
    }
    if (typeof tourFocus.mainScroll === 'number') {
      const main = document.querySelector('.v2-main');
      if (main) main.scrollTop = tourFocus.mainScroll;
    }
    setTourFocus(null);
  }, [tourFocus, tab, step]);
  // The How it works tour moves through the steps to show each one. It asks by event
  // rather than reaching into this component; every step's settings are kept, and the
  // tour puts the wizard back on the step it started from when it ends.
  useEffect(() => {
    const onGoto = (e) => {
      const n = Number(e.detail);
      if (Number.isInteger(n) && n >= 0 && n < WIZARD_STEPS.length) setStep(n);
    };
    window.addEventListener("v2:wizard-step", onGoto);
    return () => window.removeEventListener("v2:wizard-step", onGoto);
  }, []);
  const [allSettings, setAllSettings] = useState(false);
  const [confirm, setConfirm] = useState(null);
  // Which block was copied, not merely whether one was — two Copy buttons now.
  const [copied, setCopied] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [generatedKey, setGeneratedKey] = useState("");
  // The widget being edited, or "" for a new one. Editing PUTs to the existing key so
  // the embed already pasted into someone's site picks the change up; creating mints a
  // new key and a new embed.
  const [editingKey, setEditingKey] = useState("");
  const [paletteSession, setPaletteSession] = useState(0);

  // The same shape the v1 creator posts, because both write to POST /s2/api/widgets and
  // the rendered widget reads these keys. Two values here were previously wrong rather
  // than merely missing: layout was "modal" and displayMode was "button", neither of
  // which the renderer understands — it expects split/inline and inline/new-tab.
  const [cfg, setCfg] = useState({
    // Which lead magnet this is. Decides the modules the scan runs and seeds the copy.
    reportType: DEFAULT_TYPE,
    showCompanyName: false,
    appearance: { ...appearanceTools.defaults, transparent: true, padding: 0, showHeading: false },

    // Basic
    title: "How exposed is your domain?",
    subtitle: "Free scan, results in two minutes.",
    logoUrl: "",

    // Layout and theme. v1 defaults layout to "split", which matches none of its own
    // layout cards, so nothing is selected until you click one. "full" is the card its
    // UI shows selected and is the sensible default.
    layout: "full",
    theme: "light",

    // Form
    buttonText: "Scan my domain",
    buttonColor: ACCENTS[0],
    collectEmail: true,
    collectCompany: true,

    // Display. "new-tab" opens the full report page on submit; "inline" keeps the
    // visitor on the host page and renders the results inside the widget itself —
    // handleScanSubmit in widget.bundle.js branches on exactly these two values.
    displayMode: "new-tab",
    themeToggle: false,
    backgroundPattern: true,

    // Sent so the payload matches v1's exactly, but with no control of their own: v1
    // gates the trigger fields on displayMode === "modal", which its select never
    // offers, and its webhook block is commented out. Surfacing either here would be
    // inventing settings the widget does not actually read.
    triggerText: "Run Security Scan",
    triggerPosition: "bottom-right",
    autoOpen: false,
    openDelay: 0,
    webhookUrl: "",

    // The domain field's placeholder. Blank keeps widget.bundle.js's own "example.com".
    inputPlaceholder: "",

    // Report colors
    colors: { primary: "", background: "", inputBackground: "", text: "" },
  });
  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const setAppearance = (k, v) => setCfg(c => ({ ...c, appearance: { ...appearanceTools.defaults, ...c.appearance, [k]: v } }));
  const setColor = (k, v) => setCfg((c) => ({ ...c, colors: { ...c.colors, [k]: v } }));

  // A new widget is built in steps; an existing one opens the full panel, because someone
  // returning to a live widget wants the one setting they came to change, not five screens
  // of Next. `allSettings` is the escape hatch out of the wizard for the same reason.
  const wizard = !editingKey && !allSettings;
  const activePanel = wizard ? WIZARD_STEPS[step].key : tab;
  const onReview = wizard && step === REVIEW_STEP;

  // Switching type re-seeds the copy, but only fields still holding another type's
  // default. Overwriting a title someone wrote themselves because they changed their
  // mind about which report to offer would be the worst kind of helpful.
  const pickType = (id) => {
    const next = leadMagnetType(id);
    setCfg((c) => {
      const isDefault = (value, key) =>
        !value || LEAD_MAGNET_TYPES.some((t) => t[key] === value);
      return {
        ...c,
        reportType: id,
        title: isDefault(c.title, "title") ? next.title : c.title,
        subtitle: isDefault(c.subtitle, "subtitle") ? next.subtitle : c.subtitle,
        buttonText: isDefault(c.buttonText, "buttonText") ? next.buttonText : c.buttonText,
      };
    });
  };

  // Report branding.
  //
  // The report this widget produces is branded from the tenant row, which lives two
  // screens away under Settings -> Branding and is shared by every widget. Rather than
  // move those fields here — other reports use them too — the form is pre-filled with
  // the workspace's values and only the ones actually changed are saved as this widget's
  // overrides. A field left as the workspace value keeps following it, so changing the
  // office phone number once still changes it everywhere.
  const { tenant } = useTenant();
  const [branding, setBranding] = useState(null);
  const [colorDraft, setColorDraft] = useState("");
  const setBrand = (k, v) => setBranding((b) => ({ ...b, [k]: v }));

  const workspace = useMemo(() => ({
    tenant_name: tenant?.tenant_name || "",
    website: tenant?.website || "",
    address: tenant?.address || "",
    contact_email: tenant?.contact_email || "",
    contact_phone: tenant?.contact_phone || "",
    report_tagline: tenant?.report_tagline || "",
    // Not tenant?.report_style: the workspace no longer chooses one. Every widget starts
    // from the single-page report and stores a deck as its own override.
    report_style: "pdf",
    brand_colors: Array.isArray(tenant?.brand_colors) ? tenant.brand_colors : [],
  }), [tenant]);

  useEffect(() => {
    if (tenant && !branding) setBranding({ ...workspace });
  }, [tenant, branding, workspace]);

  // Only what differs from the workspace. Everything else is left out of the payload
  // entirely, which is what the server reads as "inherit".
  const brandingOverrides = () => {
    if (!branding) return undefined;
    const out = {};
    BRANDING_TEXT_FIELDS.forEach(([k]) => {
      const v = (branding[k] || "").trim();
      if (v && v !== (workspace[k] || "").trim()) out[k] = v;
    });
    if (branding.report_style && branding.report_style !== workspace.report_style) {
      out.report_style = branding.report_style;
    }
    const colors = (branding.brand_colors || []).filter((c) => HEX.test(c));
    if (colors.join(",") !== workspace.brand_colors.join(",") && colors.length) {
      out.brand_colors = colors;
    }
    return Object.keys(out).length ? out : undefined;
  };
  const overrideCount = Object.keys(brandingOverrides() || {}).length;

  // The text/background pair the widget will actually render with, once the Report colors
  // overrides are laid over the theme.
  const contrast = contrastCheck(cfg.theme, cfg.colors);

  // Follows the Layout tab's theme, so switching Light -> Cyber re-points the blank
  // Report colors fields at Cyber's colours rather than stranding them on Light's.
  const defaults = themeDefaults(cfg.theme);

  // The embed snippets.
  //
  // Both are v1's, verbatim (components/WidgetCreator.jsx). v2 had invented its own —
  // `<script src=".../widget.js" data-key="...">` — and every part of that was wrong:
  // there is no /widget.js route on any host, so it 404'd at nginx before reaching a
  // service, and the bundle has never read a data-key attribute.
  //
  // The route that exists is GET /s2/public/widget/:key, which returns a small HTML page
  // that sets window.WIDGET_KEY and then loads /s2/widget-bundle.
  //
  // Origin is taken from the current page rather than hardcoded to
  // app.pipsqueaknetscan.com the way v1 does it: a widget built on dev otherwise emits a
  // production URL, so the key it just minted is unknown to the host it points at.
  const widgetUrl = generatedKey ? `${window.location.origin}/s2/public/widget/${generatedKey}` : "";

  // Optional programmatic calls, emitted only when there is something to say — matching
  // v1, which leaves the line out entirely rather than emitting a no-op.
  const controlLines = () => [
    `window.PipsqueakWidget?.setTheme('${cfg.theme}');`,
    cfg.colors.primary ? `window.PipsqueakWidget?.setColors(${JSON.stringify(cfg.colors)});` : null,
  ].filter(Boolean).join("\n");

  const PLACEHOLDER = "<!-- Generate the embed code to get a key for this widget. -->";

  const iframeEmbed = generatedKey ? `<iframe
  src="${widgetUrl}"
  style="${widgetFrameStyle(cfg.appearance)}"
  title="Security Scanner"
  data-pipsqueak-widget="${generatedKey}"
></iframe>

<script>
${widgetResizeScript(generatedKey, widgetUrl)}

// Programmatic control (optional)
${controlLines()}
</script>` : PLACEHOLDER;

  // GoHighLevel's funnel and website builders take a Custom JS/HTML element, and what
  // works there is narrower than a normal page:
  //   * one self-contained block — the builder stores the element as a single fragment,
  //     so the resize listener has to travel with the iframe rather than sit in a
  //     site-wide script;
  //   * no reliance on the parent page's CSS — GHL wraps the element in its own row and
  //     column, and a percentage height collapses to nothing inside them;
  //   * a real starting height — the element is measured before our first resize message
  //     arrives, and a 0px iframe makes the section look empty in the editor.
  // Resize by the sending window so repeated widgets on one funnel remain independent.
  const ghlEmbed = generatedKey ? `<!-- Pipsqueak attack surface scanner -->
<div style="width:100%;max-width:${cfg.layout === "minimal" ? "520" : "640"}px;margin:0 auto;">
  <iframe
    data-pipsqueak-widget="${generatedKey}"
    src="${widgetUrl}"
    title="Security Scanner"
    loading="lazy"
    style="${widgetFrameStyle(cfg.appearance)}"
  ></iframe>
</div>

<script>
${widgetResizeScript(generatedKey, widgetUrl)}
</script>` : PLACEHOLDER;

  const scriptEmbed = generatedKey ? `<script>
(function() {
  const script = document.createElement('script');
  script.src = '${widgetUrl}';
  script.async = true;
  script.onload = function() {
    if (window.PipsqueakWidget) {
      window.PipsqueakWidget.setTheme('${cfg.theme}');${cfg.colors.primary ? `
      window.PipsqueakWidget.setColors(${JSON.stringify(cfg.colors)});` : ""}
    }
  };
  document.head.appendChild(script);
})();
</script>

<div id="widget-root"></div>` : PLACEHOLDER;

  // ── API customization ───────────────────────────────────────────────────────
  //
  // v1 carried this block (components/WidgetCreator.jsx) and v2 dropped it, so anyone
  // wanting to call the scanner directly rather than embed the widget had nothing to
  // go on. Restored here, with v1's two mistakes corrected:
  //
  //   1. v1 documents POST /s2/api/domain-scan. /api is mounted as the JWT-protected
  //      router (Report Management Service/server.js), so that path hits verifyToken
  //      and 401s before the handler ever reads widgetKey -- a valid key looks broken.
  //      The widget-key route is POST /s2/public/domain-scan (routes/Public.js).
  //   2. v1 hardcodes app.pipsqueaknetscan.com, which hands a dev-minted key a
  //      production URL that has never heard of it. Origin comes from the page, the
  //      same way widgetUrl above does it.
  //
  // No placeholder branch: the whole section is withheld until there is a key, so this
  // is only ever built with a real one.
  // `modules` is deliberately absent from the body. Omitted, the server runs exactly the
  // modules this widget's lead magnet type defines — the same set the preview advertises.
  // The snippet used to hardcode the full attack-surface list, which silently overrode the
  // type (an explicit array wins) and told anyone copying it that a Website health widget
  // scans DNS, email and typosquatting.
  const apiExample = `// JavaScript Example
fetch('${window.location.origin}/s2/public/domain-scan', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
  },
  body: JSON.stringify({
    widgetKey: '${generatedKey}',
    target: 'example.com'
    // This widget runs: ${leadMagnetType(cfg.reportType).modules.join(', ')}
    // Send a "modules" array only to override that for one call.
  })
})
.then(response => response.json())
.then(data => {
  console.log('Scan results:', data);
  console.log('Risk score:', data.riskScore);
});`;

  const generate = async () => {
    setSaving(true); setSaveError("");
    try {
      const { colors, showCompanyName, ...config } = cfg;
      // v1 omits the object entirely when every field is blank, so the server keeps its
      // own defaults instead of being handed three empty strings.
      const payload = {
        ...config,
        appearance: cfg.appearance || showCompanyName ? { ...cfg.appearance, showCompanyName } : null,
        branding: brandingOverrides(),
        colors: colors.primary || colors.background || colors.inputBackground || colors.text || colors.websitePalette ? colors : undefined,
      };
      // `generatedKey` counts as much as `editingKey` here. A widget published from the
      // wizard leaves editingKey empty — that is what keeps the wizard on its review step
      // rather than flipping to edit mode — so a second save from the full settings panel
      // used to mint a whole new widget instead of updating the one just created. Silent,
      // and it leaves a trail of near-identical unused widgets behind.
      const existing = editingKey || generatedKey;
      const key = existing ? await update(existing, payload) : await create(payload);
      setGeneratedKey(key || existing || "");
    }
    catch (e) { setSaveError(e.message || (editingKey ? "Could not save the widget." : "Could not create the widget.")); }
    finally { setSaving(false); }
  };

  // Load a saved widget back into the builder. The row is the widgets table as the API
  // returns it, so the JSON columns arrive as strings on some drivers and objects on
  // others — both are handled rather than assumed.
  const parseJson = (v, fallback) => {
    if (!v) return fallback;
    if (typeof v === "object") return v;
    try { return JSON.parse(v) || fallback; } catch { return fallback; }
  };

  const editWidget = (w) => {
    setPaletteSession(previous => previous + 1);
    const colors = parseJson(w.colors, {});
    const wb = parseJson(w.branding, {});
    setCfg((c) => ({
      ...c,
      reportType: w.reportType || DEFAULT_TYPE,
      appearance: appearanceTools.isCustomized(w.appearance) ? appearanceTools.normalize(w.appearance) : null,
      showCompanyName: !!appearanceTools.normalize(w.appearance)?.showCompanyName,
      title: w.title || "",
      subtitle: w.subtitle || "",
      logoUrl: w.logoUrl || "",
      layout: w.layout || c.layout,
      theme: w.theme || c.theme,
      displayMode: w.displayMode || c.displayMode,
      buttonText: w.buttonText || c.buttonText,
      buttonColor: w.buttonColor || c.buttonColor,
      inputPlaceholder: w.inputPlaceholder || "",
      collectEmail: w.collectEmail ?? c.collectEmail,
      collectCompany: w.collectCompany ?? c.collectCompany,
      themeToggle: !!w.themeToggle,
      backgroundPattern: w.backgroundPattern ?? c.backgroundPattern,
      triggerText: w.triggerText || c.triggerText,
      triggerPosition: w.triggerPosition || c.triggerPosition,
      autoOpen: !!w.autoOpen,
      openDelay: w.openDelay ?? 0,
      webhookUrl: w.webhookUrl || "",
      colors: {
        websitePalette: colors.websitePalette,
        primary: colors.primary || "",
        background: colors.background || "",
        inputBackground: colors.inputBackground || "",
        text: colors.text || "",
      },
    }));
    // Overrides the widget stored, laid over the workspace values; anything it does not
    // override keeps following Settings -> Branding, same as when it was created.
    setBranding({ ...workspace, ...wb });
    setEditingKey(w.widgetKey);
    setGeneratedKey(w.widgetKey);
    setTab("Basic");
    // An existing widget opens in the full panel: someone editing a live widget is after
    // one setting, and walking them through five steps to reach it would be worse than
    // the tab wall the wizard exists to avoid.
    setAllSettings(false);
    setStep(0);
    setSaveError("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const newWidget = () => {
    setPaletteSession(previous => previous + 1);
    setCfg(c => ({ ...c, colors: { ...c.colors, websitePalette: undefined } }));
    setEditingKey("");
    setGeneratedKey("");
    setSaveError("");
    // Back to step one of the wizard. Without this, "Build another widget" left the
    // previous widget's key on screen and the review step still reading "published".
    setAllSettings(false);
    setStep(0);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // /scanner/lead-magnet/<widgetKey> opens the builder on that widget. Waits for the list
  // for the same reason the flyout intent below does: the key names a row that does not
  // exist until it has loaded. Runs once per key, so re-rendering does not reload the
  // widget over edits in progress.
  useEffect(() => {
    if (!editKey || loading || editKey === editingKey) return;
    const w = widgets.find((x) => x.widgetKey === editKey);
    if (w) editWidget(w);
    // editWidget is recreated every render; the key is the trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editKey, loading, widgets]);

  // A request from the rail flyout, passed down by LeadMagnetScreen. Waits for the list:
  // an edit names a widget that is not there until it has loaded.
  useEffect(() => {
    if (!pendingIntent || loading) return;
    if (pendingIntent.type === "new") newWidget();
    if (pendingIntent.type === "edit") {
      const w = widgets.find((x) => x.widgetKey === pendingIntent.widgetKey);
      if (w) editWidget(w);
    }
    onIntentHandled();
    // newWidget and editWidget are recreated every render; the request is the trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingIntent, loading]);

  const copy = async (snippet, which) => {
    try { await navigator.clipboard.writeText(snippet); recordActivity('lead_magnets.embed_copied'); setCopied(which); setTimeout(() => setCopied(null), 1800); }
    catch { /* clipboard blocked; the snippet is on screen to copy by hand */ }
  };

  // ── Shared control groups ───────────────────────────────────────────────────────────
  //
  // Defined once and rendered by both the wizard and the tab panel. The two modes arrange
  // them differently — the wizard gives each step the full width of the pane, the panel
  // stacks them in a 320px column — but a control exists in exactly one place, so the two
  // can never drift.
  const typeGrid = (
    <div className="v2-typepick">
      {LEAD_MAGNET_TYPES.map((t) => (
        <button key={t.id} type="button"
                className={`v2-typepick-card${cfg.reportType === t.id ? " on" : ""}`}
                onClick={() => pickType(t.id)}>
          <span className="v2-typepick-name">{t.name}</span>
          <span className="v2-typepick-blurb">{t.blurb}</span>
          <span className="v2-typepick-meta">
            Asks for {t.input === "url" ? "a website URL" : "a company domain"}
          </span>
        </button>
      ))}
    </div>
  );

  const copyFields = (
    <SettingsGroup title="Content" description="Heading, description and logo">
      <Field label="Title"><input className="v2-input" value={cfg.title} onChange={(e) => set("title", e.target.value)} /></Field>
      <Field label="Subtitle"><input className="v2-input" value={cfg.subtitle} onChange={(e) => set("subtitle", e.target.value)} /></Field>
      <Field label="Logo URL" hint="Optional. Shown above the title.">
        <input className="v2-input" value={cfg.logoUrl} placeholder="https://yourcompany.com/logo.png"
               onChange={(e) => set("logoUrl", e.target.value)} />
      </Field>
    </SettingsGroup>
  );

  const appearance = { ...appearanceTools.defaults, ...cfg.appearance };
  const [previewBackground, setPreviewBackground] = useState('');
  const [previewMobile, setPreviewMobile] = useState(false);
  const appearanceSelect = (key, label, choices) => (
    <Field label={label} key={key}><select className="v2-input" aria-label={label} value={appearance[key]} onChange={e => setAppearance(key, e.target.value)}>
      {choices.map(([value, name]) => <option key={value} value={value}>{name}</option>)}
    </select></Field>
  );
  const appearanceRange = (key, label, min, max) => (
    <Field key={key} label={`${label}: ${appearance[key]}px`}>
      <input type="range" aria-label={label} min={min} max={max} value={appearance[key]} onChange={e => setAppearance(key, Number(e.target.value))} />
    </Field>
  );
  const appearanceToggle = (key, label) => (
    <SettingRow key={key} name={label}><Toggle checked={appearance[key]} onChange={v => setAppearance(key, v)} ariaLabel={label} /></SettingRow>
  );
  const appearanceColor = (key, label) => (
    <Field key={key} label={label}>
      <ColorField label={label} value={appearance[key]} disabled={key === 'background' && appearance.transparent} allowClear={false} onChange={value => setAppearance(key, value)} />
    </Field>
  );
  const websitePalette = <SettingsGroup title="Website palette" description="Reuse colors picked from the website preview"><WebsitePalette /></SettingsGroup>;
  const switchToCustomization = () => setCfg(c => {
    const palette = themeVars(c.theme, c.colors);
    const initial = appearanceTools.normalize({ ...appearanceTools.defaults,
      transparent: c.layout !== 'full', padding: c.layout === 'full' ? 24 : 0,
      showHeading: c.layout === 'full', showBenefits: c.layout === 'full',
      inputTransparent: false, inputBackground: palette['--input-bg'] || palette['--bg-secondary'],
      background: palette['--surface'], textColor: palette['--text'],
      inputText: palette['--text'], placeholderColor: palette['--text-light'],
      borderColor: palette['--border'], focusColor: palette['--primary'],
      buttonBackground: c.buttonColor || palette['--primary'],
    });
    // Keep the original layout so reverting never changes the old embed's layout.
    return { ...c, appearance: { ...appearanceTools.defaults, ...initial } };
  });
  const originalModeNotice = (
    <div className="v2-cfg-section">
      <p className="v2-hint">This widget uses its original appearance. Editing its content or saving keeps that mode. Switch only when you want the new styling controls, then save to publish the change.</p>
      <Button onClick={switchToCustomization}>Switch to new customization</Button>
    </div>
  );

  const layoutFields = (
    <>
      {!cfg.appearance ? originalModeNotice : <div className="v2-cfg-section">
        <Button onClick={() => set('appearance', null)}>Use original appearance</Button>
      </div>}
      <SettingsGroup title="Layout" description="Content, width and visibility" defaultOpen>
      <Field label="Widget layout" hint="Choose how much content surrounds the form. Existing embed URLs stay the same.">
        <select className="v2-input" aria-label="Widget layout" value={cfg.appearance ? (appearance.showHeading ? (appearance.showBenefits ? 'full' : 'card') : 'form') : `legacy-${cfg.layout === 'full' ? 'full' : 'minimal'}`} onChange={e => {
          const preset = e.target.value;
          if (preset.startsWith('legacy-')) { set('layout', preset.slice(7)); return; }
          if (!cfg.appearance) return;
          setCfg(c => ({ ...c, appearance: { ...appearanceTools.defaults, ...c.appearance,
            transparent: c.appearance?.transparent ?? true, padding: preset === 'form' ? 0 : 24,
            showHeading: preset !== 'form', showBenefits: preset === 'full' } }));
        }}>
          {cfg.appearance ? <>
            <option value="form">Form only</option>
            <option value="card">Compact card</option>
            <option value="full">Full section</option>
          </> : <>
            <option value="legacy-full">Original full card</option>
            <option value="legacy-minimal">Original minimal form</option>
          </>}
        </select>
      </Field>

        {cfg.appearance && <>
          {appearanceRange('maxWidth', 'Maximum width', 280, 1200)}
          {appearanceToggle('showHeading', 'Show heading and logo')}
          {appearanceToggle('showBenefits', 'Show scan benefits')}
        </>}
      </SettingsGroup>
      {cfg.appearance && <>
        <SettingsGroup title="Typography" description="Font, text size and alignment">
          {appearanceSelect('font', 'Font', [['system', 'System sans serif'], ['arial', 'Arial / Helvetica'], ['serif', 'Georgia / serif'], ['mono', 'Monospace']])}
          {appearanceRange('fontSize', 'Text size', 12, 24)}
          {appearanceSelect('alignment', 'Heading alignment', [['left', 'Left'], ['center', 'Center'], ['right', 'Right']])}
          {appearanceColor('textColor', 'Widget text')}
        </SettingsGroup>
        <SettingsGroup title="Card and background" description="Background, padding, corners and shadow" tourId="widget-card-settings">
          {appearanceToggle('transparent', 'Transparent background')}
          {appearanceColor('background', 'Card background')}
          {appearanceRange('padding', 'Card padding', 0, 64)}
          <p className="v2-hint">Iframe margins add space outside the widget. Padding adds space inside the card.</p>
          {appearanceRange('marginTop', 'Top margin', 0, 160)}
          {appearanceRange('marginBottom', 'Bottom margin', 0, 160)}
          {appearanceRange('marginLeft', 'Left margin', 0, 160)}
          {appearanceRange('marginRight', 'Right margin', 0, 160)}
          {appearanceRange('cardRadius', 'Card corners', 0, 40)}
          {appearanceToggle('shadow', 'Card shadow')}
        </SettingsGroup>
      </>}
      {websitePalette}
      <SettingsGroup title="Colour theme" description="Choose a base palette">
        <div className="v2-themepick">
          {THEMES.map((t) => (
            <button key={t.id} type="button"
                    className={`v2-themepick-item${cfg.theme === t.id ? " on" : ""}`}
                    aria-pressed={cfg.theme === t.id}
                    style={{ background: t.bg, color: t.ink }}
                    onClick={() => set("theme", t.id)}>
              <span className="v2-themepick-name">{t.name}</span>
              <span className="v2-themepick-dots">
                <span className="v2-themepick-dot" style={{ background: t.primary }} />
                <span className="v2-themepick-dot" style={{ background: t.bg }} />
              </span>
            </button>
          ))}
        </div>

      </SettingsGroup>
    </>
  );
  const formFields = (
    <>
      {leadMagnetType(cfg.reportType).asksCompany && <SettingsGroup title="Company name" description="Optional details for company matching" defaultOpen>
        <SettingRow name="Show company name field"><Toggle checked={cfg.showCompanyName} onChange={v => set('showCompanyName', v)} ariaLabel="Show company name field" /></SettingRow>
        <p className="v2-hint">Off by default. When enabled, visitors can optionally enter their registered company name to improve discovery matching.</p>
      </SettingsGroup>}
      <SettingsGroup title="Submit behavior" description="Button position, Enter to scan and spacing" defaultOpen tourId="widget-submit-settings">
        {cfg.appearance ? <>
          {appearanceSelect('arrangement', 'Submit style', [['stacked', 'Button below input'], ['row', 'Button beside input'], ['inset', 'Button inside input'], ['enter', 'No button - press Enter']])}
          {appearanceRange('gap', 'Form spacing', 4, 32)}
          {appearanceRange('inputHeight', 'Control height', 40, 72)}
        </> : originalModeNotice}
      </SettingsGroup>
      <SettingsGroup title="Input" description="Label, placeholder, fill, borders and focus" tourId="widget-input-settings">
        {cfg.appearance && <>
          <Field label="Input label" hint="Optional. Leave blank to hide the visible label.">
            <input className="v2-input" aria-label="Input label" maxLength={80} value={appearance.label} onChange={e => setAppearance('label', e.target.value)} />
          </Field>
        </>}
        <Field label="Domain field placeholder" hint="Leave blank for example.com.">
          <input className="v2-input" value={cfg.inputPlaceholder} maxLength={100} placeholder="example.com" onChange={e => set('inputPlaceholder', e.target.value)} />
        </Field>
        {cfg.appearance && <>
          {appearanceSelect('inputStyle', 'Input style', [['outlined', 'Outlined'], ['filled', 'Filled'], ['underline', 'Underline']])}
          {appearanceToggle('inputTransparent', 'Transparent input')}
        </>}
        <Field label="Form field background">
          <ColorField label="Form field background" disabled={!!cfg.appearance && appearance.inputTransparent} value={cfg.appearance ? appearance.inputBackground : cfg.colors.inputBackground} fallback={defaults.inputBackground}
            onChange={v => cfg.appearance ? setAppearance('inputBackground', v) : setColor('inputBackground', v)} />
        </Field>
        {cfg.appearance && <>
          {appearanceRange('inputRadius', 'Input corners', 0, 36)}
          {appearanceColor('inputText', 'Input text')}
          {appearanceColor('placeholderColor', 'Placeholder')}
          {appearanceColor('borderColor', 'Input border')}
          {appearanceColor('focusColor', 'Focus ring')}
        </>}
      </SettingsGroup>
      {(!cfg.appearance || appearance.arrangement !== 'enter') && <SettingsGroup title="Button" description="Text, style, colors and corners" tourId="widget-button-settings">
        <Field label="Button text"><input className="v2-input" value={cfg.buttonText} onChange={e => set('buttonText', e.target.value)} /></Field>
        {cfg.appearance && appearanceSelect('buttonStyle', 'Button style', [['solid', 'Solid'], ['outline', 'Outlined'], ['soft', 'Soft tint']])}
        <Field label="Button colour"><ColorField value={cfg.appearance ? appearance.buttonBackground : cfg.buttonColor}
          onChange={v => cfg.appearance ? setAppearance('buttonBackground', v) : set('buttonColor', v)} swatches={ACCENTS} /></Field>
        {cfg.appearance && <>
          {appearanceColor('buttonText', 'Button text color')}
          {appearanceRange('buttonRadius', 'Button corners', 0, 36)}
        </>}
      </SettingsGroup>}
      {websitePalette}
      <SettingsGroup title="Lead capture" description="Details requested after the scan">
        <p className="v2-hint">Contact details are requested after results are ready.</p>
        <SettingRow name="Collect email address"><Toggle checked={cfg.collectEmail} onChange={v => set('collectEmail', v)} ariaLabel="Collect email address" /></SettingRow>
      </SettingsGroup>
    </>
  );

  const displayFields = (
    <>
      <Field label="Display mode">
        <select className="v2-input" value={cfg.displayMode} onChange={(e) => set("displayMode", e.target.value)}>
          <option value="new-tab">New tab</option>
          <option value="inline">Inline widget</option>
        </select>
      </Field>
      <p className="v2-hint" style={{ margin: "-.25rem 0 .75rem" }}>
        {cfg.displayMode === "inline"
          ? "Results render inside the widget, keeping the visitor on your page."
          : "Running a scan opens the visitor's full security report in a new tab."}
      </p>
      <SettingRow name="Include theme toggle button">
        <Toggle checked={cfg.themeToggle} onChange={(v) => set("themeToggle", v)} ariaLabel="Include theme toggle button" />
      </SettingRow>
      <SettingRow name="Add background pattern">
        <Toggle checked={cfg.backgroundPattern} onChange={(v) => set("backgroundPattern", v)} ariaLabel="Add background pattern" />
      </SettingRow>
    </>
  );

  // The rendering of the widget itself. Shared for the same reason as the controls above:
  // the wizard shows it beside each step, the tab panel keeps it in the right-hand pane.
  const previewButton = <button type="button" className="v2-wp-cta" style={{ background: cfg.buttonColor }} disabled>
    {cfg.buttonText || "Start Free Scan"}
  </button>;
  const widgetPreview = (
    <div className="v2-widget-reference-layout">
      <section className="v2-widget-preview-pane" aria-label="Widget design preview">
        <header className="v2-studio-panel-head">
          <div className="v2-studio-panel-title"><span className="v2-studio-panel-icon"><TbSparkles size={18} /></span><div><h3>Widget preview</h3><p>{leadMagnetType(cfg.reportType).name}</p></div></div>
          <span className="v2-studio-live"><i />Live</span>
        </header>
        <div className="v2-preview-toolbar">
          <div className="v2-preview-devices" role="group" aria-label="Preview device">
            <button type="button" aria-label="Desktop preview" aria-pressed={!previewMobile} className={!previewMobile ? 'on' : ''} onClick={() => setPreviewMobile(false)}><TbDeviceDesktop size={16} /><span>Desktop</span></button>
            <button type="button" aria-label="Mobile preview" aria-pressed={previewMobile} className={previewMobile ? 'on' : ''} onClick={() => setPreviewMobile(true)}><TbDeviceMobile size={16} /><span>Mobile</span></button>
          </div>
          <div className="v2-preview-backdrop"><button type="button" className="v2-preview-checker" aria-label="Show transparency grid" aria-pressed={!previewBackground} title="Show transparency grid" onClick={() => setPreviewBackground('')} /><label title="Preview background"><span>Canvas</span><input aria-label="Preview background" type="color" value={previewBackground || '#ffffff'} onChange={e => setPreviewBackground(e.target.value)} /></label><ScreenColorPicker onPick={setPreviewBackground} label="Pick preview background from screen" /></div>
          <PaletteSwatches onChoose={setPreviewBackground} value={previewBackground} label="Preview background" />
        </div>
        <div className="v2-preview-canvas" style={previewBackground ? { backgroundColor: previewBackground, backgroundImage: 'none' } : undefined}>
    <div className="v2-wp-stage v2-appearance-preview" style={{ ...themeVars(cfg.theme, cfg.colors), width: previewMobile ? 320 : '100%', maxWidth: '100%' }}>
      <style>{appearanceTools.companyCss('.v2-appearance-preview') + appearanceTools.css(cfg.appearance, '.v2-appearance-preview', true)}</style>
      <div className={`v2-wp v2-wp--${cfg.layout}`}>
        {(cfg.appearance || cfg.layout === "full") && (
          <div className="v2-wp-head">
            {cfg.logoUrl && <img src={cfg.logoUrl} alt="" className="v2-wp-logo" />}
            <div className="v2-wp-title">{cfg.title || "Check Your Security Score"}</div>
            <div className="v2-wp-sub">
              {cfg.subtitle || "A real-time external security assessment of your domain."}
            </div>
            <div className="v2-wp-chips">
              {(leadMagnetType(cfg.reportType).chips || STAT_CHIPS).map((c) => (
                <span key={c} className="v2-wp-chip"><i aria-hidden="true" />{c}</span>
              ))}
            </div>
          </div>
        )}

        <div className="v2-wp-form">
          <div className="v2-wp-target">
            {cfg.appearance && appearance.label && <label htmlFor="scan-target-preview" className="v2-wp-label">{appearance.label}</label>}
            <div className="appearance-control">
              <input id="scan-target-preview" className="v2-wp-input" readOnly aria-label="Scan target preview"
                   placeholder={cfg.inputPlaceholder.trim() || leadMagnetType(cfg.reportType).inputPlaceholder || "example.com"} />
              {cfg.appearance && appearance.arrangement === 'inset' && previewButton}
            </div>
            {cfg.appearance && appearance.arrangement === 'enter' && <div className="v2-enter-hint">Press Enter to scan</div>}
          </div>
          {leadMagnetType(cfg.reportType).asksCompany && cfg.showCompanyName && (
            <div className="scan-company-group">
              <label className="v2-wp-label">Registered company name (optional)</label>
              <input className="v2-wp-input" readOnly placeholder="Example Company Ltd" aria-label="Company name preview" />
              <span className="company-hint">Helps match company records. You can leave this blank.</span>
            </div>
          )}
          {/* The optional address field the widget adds on Dark web and Email security. */}
          {leadMagnetType(cfg.reportType).asksEmail && (
            <input className="v2-wp-input" readOnly
                   placeholder="Staff emails to check (optional)" />
          )}
          {(!cfg.appearance || appearance.arrangement !== 'inset') && previewButton}
          {(cfg.appearance || cfg.layout === "full") && (
            <div className="v2-wp-trust">{TRUST_BADGES.map((b) => <span key={b}>{b}</span>)}</div>
          )}
        </div>


      </div>
    </div>
      </div>
        <div className="v2-preview-caption"><span className="v2-preview-caption-dot" />Changes appear as you edit. Canvas color is preview only.</div>
      </section>
      <WebsiteReferencePreview />
    </div>
  );

  const selectedType = leadMagnetType(cfg.reportType);

  // ── The wizard ──────────────────────────────────────────────────────────────────────
  //
  // In the main pane, not the 320px config column it started in. In the column the step
  // content pushed Next below the fold, which is the one place a wizard cannot afford to
  // put its primary action — a tester simply could not find it.
  //
  // So the actions live in the step header, at the top of the pane and visible before any
  // scrolling, and are repeated at the foot for anyone who has scrolled a long step.
  const wizardActions = (place) => (
    <div className={`v2-wz-actions v2-wz-actions--${place}`}>
      <Button onClick={() => setStep((n) => Math.max(0, n - 1))} disabled={step === 0}>
        <TbArrowLeft size={15} /> Back
      </Button>
      {step < REVIEW_STEP && (
        <Button variant="primary" onClick={() => setStep((n) => Math.min(REVIEW_STEP, n + 1))}>
          Next <TbArrowRight size={15} />
        </Button>
      )}
    </div>
  );

  const wizardPane = (
    // data-tour-page: the guided setup has its own How it works tour, which walks through
    // each step in turn (shell/tours.js).
    <div className="v2-wz" data-tour-page="lead-magnet-wizard">
      <ol className="v2-wz-steps" aria-label="Setup steps">
        {WIZARD_STEPS.map((s, i) => (
          <li key={s.key}>
            <button type="button" disabled={i > step}
                    className={`v2-wz-step${i === step ? " on" : ""}${i < step ? " done" : ""}`}
                    aria-current={i === step ? "step" : undefined}
                    onClick={() => setStep(i)}>
              <span className="v2-wz-step-n">{i < step ? <TbCheck size={13} /> : i + 1}</span>
              <span>{s.label}</span>
            </button>
          </li>
        ))}
      </ol>

      <div className="v2-wz-head">
        <div>
          <div className="v2-wz-count">Step {step + 1} of {WIZARD_STEPS.length}</div>
          <h2 className="v2-wz-title">{WIZARD_STEPS[step].title}</h2>
          <p className="v2-wz-note">{WIZARD_STEPS[step].note}</p>
        </div>
        {wizardActions("top")}
      </div>

      <Alert>{saveError || error}</Alert>

      {/* Step 1 is the full width of the pane: the grid is the decision, and everything
          after it is a consequence of the choice made here. */}
      {step === 0 && (
        <>
          {typeGrid}
          <div className="v2-wz-chosen">
            <div>
              <div className="v2-wz-chosen-label">Selected</div>
              <div className="v2-wz-chosen-name">{selectedType.name}</div>
              <p className="v2-wz-chosen-blurb">{selectedType.blurb}</p>
            </div>
            <dl className="v2-wz-chosen-facts">
              <div><dt>Visitor enters</dt><dd>{selectedType.input === "url" ? "A website URL" : "A company domain"}</dd></div>
              <div><dt>Checks run</dt><dd className="v2-mono">{selectedType.modules.join(", ")}</dd></div>
              <div><dt>Shown on the widget</dt><dd>{(selectedType.chips || []).join(" · ")}</dd></div>
            </dl>
          </div>
        </>
      )}

      {/* The remaining steps put the controls beside a live rendering, so a change and its
          effect are on screen together. */}
      {step > 0 && step < REVIEW_STEP && (
        <div className="v2-wz-split">
          <div className="v2-wz-controls">
            {step === 1 && <>{layoutFields}{copyFields}</>}
            {step === 2 && formFields}
            {step === 3 && displayFields}
          </div>
          <div className="v2-wz-preview">

            {widgetPreview}
          </div>
        </div>
      )}

      {step === REVIEW_STEP && (
        <div className="v2-wz-split">
          <div className="v2-wz-controls">
            <dl className="v2-review">
              <div><dt>Lead magnet</dt><dd>{selectedType.name}</dd></div>
              <div><dt>Checks run</dt><dd className="v2-mono">{selectedType.modules.join(", ")}</dd></div>
              <div><dt>Visitor enters</dt><dd>{selectedType.input === "url" ? "A website URL" : "A company domain"}</dd></div>
              <div><dt>Layout</dt><dd>{cfg.layout === "full" ? "Full card" : "Bare minimum"} · {cfg.theme}</dd></div>
              <div><dt>On submit</dt><dd>{cfg.displayMode === "inline" ? "Results inline" : "Full report in a new tab"}</dd></div>
              <div><dt>Collects</dt><dd>{cfg.collectEmail ? "Email address" : "Nothing beyond the domain"}</dd></div>
            </dl>

            {!generatedKey ? (
              <>
                <Button variant="primary" onClick={generate} disabled={saving} style={{ width: "100%" }}>
                  {saving ? "Generating…" : "Generate embed code"}
                </Button>
                <p className="v2-hint">
                  This saves the widget and mints its key. You can keep editing it
                  afterwards — the embed on your page follows the changes.
                </p>
              </>
            ) : (
              <>
                <div className="v2-review-done">
                  <TbCheck size={16} />
                  <div>
                    <strong>Widget published</strong>
                    <div className="v2-mono v2-dim" style={{ fontSize: ".75rem" }}>{generatedKey}</div>
                  </div>
                </div>
                {/* Before the embed code, because running it is how you find out whether
                    it works. This opens the hosted page the iframe embed points at — the
                    same bundle, config and scan a visitor gets — so the widget can be
                    tried end to end without touching a customer's site first. */}
                <Button variant="primary" onClick={() => window.open(widgetUrl, "_blank", "noopener")}>
                  <TbPlayerPlay size={15} /> Test this widget
                </Button>
                <p className="v2-hint">
                  Opens the live widget in a new tab. Run a scan through it to see exactly
                  what a visitor gets, including the report. Captures from here appear
                  under Leads, so use a domain you do not mind seeing in the list.
                </p>
                <p className="v2-hint">The embed code is below. Paste it into your page.</p>
                <Button onClick={() => { setAllSettings(true); setTab("Widget colors"); }}>
                  Fine-tune colours &amp; report branding
                </Button>
                <Button onClick={newWidget}>Build another widget</Button>
              </>
            )}
          </div>
          <div className="v2-wz-preview">

            {widgetPreview}
          </div>
        </div>
      )}

      {wizardActions("bottom")}
    </div>
  );


  return (
    <WebsiteColorsProvider key={paletteSession} value={cfg.colors.websitePalette} onChange={value => setColor('websitePalette', value)}>
      <div className="v2-builder-bar">
        <span className="v2-section-title">
          {editingKey ? "Editing widget" : wizard ? "New lead magnet" : "Widget builder"}
        </span>
        <span className="v2-studio-mode">{cfg.appearance ? 'Custom appearance' : 'Original appearance'}</span>
        {editingKey && (
          <>
            <span className="v2-mono v2-dim" style={{ fontSize: ".75rem" }}>{editingKey}</span>
            <Button onClick={newWidget} disabled={saving}>New widget</Button>
          </>
        )}
        {/* The way back. Leaving the wizard for the full panel used to be one-way, which
            made "Skip to all settings" a decision rather than a look — and the wizard was
            then unreachable without reloading the screen. Only offered for a widget still
            being created: editing an existing one is the tab panel's own job, and there is
            no wizard behind it to return to. Every step's state is preserved, so this
            comes back exactly where it left. */}
        {allSettings && !editingKey && (
          <Button style={{ marginLeft: "auto" }} onClick={() => setAllSettings(false)}>
            <TbArrowBackUp size={15} /> Back to guided setup
          </Button>
        )}
        {/* In the wizard, publishing belongs to the last step — a Generate button sitting
            above step one invites a half-configured widget and an embed code for it. */}
        {!wizard && (
          <Button variant="primary"
                  style={{ marginLeft: editingKey || allSettings ? 0 : "auto" }}
                  onClick={generate} disabled={saving}>
            {saving
              ? (editingKey || generatedKey ? "Saving…" : "Generating…")
              : (editingKey || generatedKey ? "Save changes" : "Generate embed code")}
          </Button>
        )}
        {wizard && (
          <Button style={{ marginLeft: "auto" }} onClick={() => { setAllSettings(true); setTab("Basic"); }}>
            Skip to all settings
          </Button>
        )}
      </div>

      {wizard ? wizardPane : (
      <div className="v2-split" data-tour-page="lead-magnet-studio" data-active-panel={tab}>
        <div className="v2-split-config">
          <div className="v2-studio-settings-head"><span>Design settings</span><small>Fine-tune your widget</small></div>
          <div className="v2-split-tabs">
            {CONFIG_TABS.map((t) => (
              <button key={t} type="button" className={`v2-split-tab${tab === t ? " on" : ""}`} onClick={() => setTab(t)}>{t}</button>
            ))}
          </div>
          <div className="v2-split-body">
            <Alert>{saveError || error}</Alert>

            {/* Each group carries the heading and one-line description the v1 creator
                shows, because the tab word alone does not say what is in it. */}
            {activePanel === "Basic" && (
              <>
                <TabHead title="Basic settings" note="What the widget says before anyone types anything." />
                <Field label="Lead magnet" hint="What this widget offers, and what it checks.">
                  {typeGrid}
                </Field>
                {copyFields}
              </>
            )}

            {activePanel === "Layout" && (
              <>
                <TabHead title="Layout &amp; theme" note="How the widget is structured and styled." />
                {layoutFields}
              </>
            )}

            {activePanel === "Form" && (
              <>
                <TabHead title="Form settings" note="Configure the form fields and button appearance." />
                {formFields}
              </>
            )}

            {activePanel === "Display" && (
              <>
                <TabHead title="Display settings" note="Control how and where your widget appears." />
                {displayFields}
              </>
            )}

            {activePanel === "Widget colors" && (
              <>
                <TabHead title="Widget colors" note="Fine-tune the colours the widget and its report render with." />
                <div className="v2-cfg-section">
                  <div className="v2-cfg-section-title">Custom colours</div>
                  <Field label="Primary colour">
                    <ColorField value={cfg.colors.primary} fallback={defaults.primary}
                                onChange={(v) => setColor("primary", v)} />
                  </Field>
                  <Field label="Background colour">
                    <ColorField value={cfg.colors.background} fallback={defaults.background}
                                onChange={(v) => setColor("background", v)} />
                  </Field>

                  <Field label="Text colour">
                    <ColorField value={cfg.colors.text} fallback={defaults.text}
                                onChange={(v) => setColor("text", v)} />
                  </Field>
                  <p className="v2-hint">
                    Each colour replaces the theme's own. Leave one blank to keep the
                    theme's value for it.
                  </p>

                  {/* The widget renders on someone else's marketing site, where nobody
                      from here will see it. Two free-form colours can put white on white,
                      so the pair is checked rather than trusted. */}
                  {!contrast.passes && (
                    <Alert tone="warning">
                      Text on background is only {contrast.ratio.toFixed(1)}:1. Below 4.5:1
                      this is hard to read for many people — worth adjusting before you embed it.
                    </Alert>
                  )}
                </div>
              </>
            )}

            {activePanel === "Report branding" && (
              <>
                <TabHead title="Report branding"
                         note="What the report this widget produces says about you." />
                {!branding ? (
                  <p className="v2-hint">Loading your workspace branding…</p>
                ) : (
                  <>
                    {/* <p className="v2-hint" style={{ margin: "0 0 .75rem" }}>
                      Pre-filled from Settings → Branding. Change a field here and this
                      widget uses your value; leave it and it keeps following the
                      workspace, so a contact detail edited there still updates this report.
                      {overrideCount > 0 && (
                        <> <strong>{overrideCount} field{overrideCount === 1 ? "" : "s"} overridden.</strong></>
                      )}
                    </p> */}

                    {BRANDING_TEXT_FIELDS.map(([key, label, placeholder]) => {
                      const overridden = (branding[key] || "").trim() !== (workspace[key] || "").trim();
                      return (
                        <Field key={key} label={label}>
                          <div className="v2-addrow">
                            <input className="v2-input" value={branding[key]} placeholder={placeholder}
                                   maxLength={255} onChange={(e) => setBrand(key, e.target.value)} />
                            {overridden && (
                              <Button onClick={() => setBrand(key, workspace[key])}>Reset</Button>
                            )}
                          </div>
                        </Field>
                      );
                    })}

                    <Field label="Report style"
                           hint="The layout a visitor gets after the scan.">
                      <select className="v2-input" value={branding.report_style}
                              onChange={(e) => setBrand("report_style", e.target.value)}>
                        <option value="pdf">PDF / single-page report</option>
                        <option value="slide_deck">Slide deck presentation</option>
                      </select>
                    </Field>

                    {/* Distinct from the Report colors tab: those paint the widget's own
                        chrome, these are the tenant palette the report cover uses —
                        brand_colors[0..2] become --primary / --secondary / --accent. */}
                    <Field label="Brand colours"
                           hint="Up to eight. The first three become the report's primary, secondary and accent.">
                      <Swatches colors={(branding.brand_colors || []).length ? branding.brand_colors : ACCENTS}
                                value={(branding.brand_colors || [])[0] || ACCENTS[0]}
                                onChange={(c) => setBrand("brand_colors",
                                  [c, ...(branding.brand_colors || []).filter((x) => x !== c)])} />
                      <div className="v2-colorfield" style={{ marginTop: ".5rem" }}>
                        <ScreenColorPicker onPick={setColorDraft} label="Pick report brand color from screen" disabled={(branding.brand_colors || []).length >= 8} />
                        <PaletteSwatches onChoose={setColorDraft} label="Report brand color" value={colorDraft} />
                      </div>
                      <div className="v2-addrow" style={{ marginTop: ".5rem" }}>
                        <input className="v2-input" value={colorDraft} placeholder="#1F7FE5" maxLength={7}
                               onChange={(e) => setColorDraft(e.target.value)}
                               onKeyDown={(e) => {
                                 if (e.key !== "Enter") return;
                                 e.preventDefault();
                                 const hex = colorDraft.trim();
                                 if (!HEX.test(hex) || (branding.brand_colors || []).includes(hex) || (branding.brand_colors || []).length >= 8) return;
                                 setBrand("brand_colors", [...(branding.brand_colors || []), hex]);
                                 setColorDraft("");
                               }} />
                        <Button disabled={!HEX.test(colorDraft.trim()) || (branding.brand_colors || []).length >= 8}
                                onClick={() => {
                                  const hex = colorDraft.trim();
                                  if ((branding.brand_colors || []).includes(hex)) return;
                                  setBrand("brand_colors", [...(branding.brand_colors || []), hex]);
                                  setColorDraft("");
                                }}>
                          Add
                        </Button>
                      </div>
                      {(branding.brand_colors || []).length > 0 && (
                        <div className="v2-colorlist">
                          {branding.brand_colors.map((c) => (
                            <span key={c} className="v2-chip">
                              <span className="v2-colordot" style={{ background: c }} />{c}
                              <button type="button" className="v2-chip-x" aria-label={`Remove ${c}`}
                                      onClick={() => setBrand("brand_colors", branding.brand_colors.filter((x) => x !== c))}>✕</button>
                            </span>
                          ))}
                        </div>
                      )}
                    </Field>

                    {overrideCount > 0 && (
                      <div style={{ marginTop: ".75rem" }}>
                        <Button onClick={() => setBranding({ ...workspace })}>
                          Reset all to workspace branding
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>

        </div>

        {/* A scaled rendering of what the bundle actually paints, not an impression of
            it. Structure, order and colour all come from widget.bundle.js:

              full     -> the card: heading, subtitle, module chips, the stacked input
                          and full-width button, the trust line, the bordered footer.
              minimal  -> compact_layout, which is the form alone: no heading, no chips,
                          no trust line, no footer.

            Only the domain input appears in either, because the real initial form has
            one field — email and company are asked for after the scan. */}
        {/* The same rendering the wizard shows, in the pane beside the settings. */}
        <div className="v2-split-preview">
          {widgetPreview}
        </div>
      </div>
      )}

      {/* Both of these are about a widget that exists. During the wizard they are three
          screens of embed snippets reading "Generate the embed code to get a key",
          sitting under a widget nobody has finished describing yet. */}
      {(!wizard || generatedKey) && <LeadDelivery webhookUrl={cfg.webhookUrl} />}

      {(!wizard || generatedKey) && (
      <div className="v2-embed">
        <div className="v2-embed-head">
          <span className="v2-section-title">Embed code</span>
          <span className="v2-embed-sub">Copy and paste these into your website.</span>
        </div>

        {/* Both forms, as v1 lists them. The iframe is recommended because it renders in
            its own document: the script tag drops the widget into the host page's DOM,
            where the site's own CSS reaches it. */}
        <div className="v2-embed-block">
          <div className="v2-embed-head">
            <span className="v2-embed-label">Iframe embed <em>Recommended</em></span>
            <Button style={{ marginLeft: "auto" }} onClick={() => copy(iframeEmbed, "iframe")} disabled={!generatedKey}>
              <TbCopy size={15} /> {copied === "iframe" ? "Copied" : "Copy"}
            </Button>
          </div>
          <pre className="v2-embed-code">{iframeEmbed}</pre>
        </div>

        {/* GoHighLevel gets its own block rather than a note under the iframe one.
            Tenants running funnels there are the largest group pasting these snippets,
            and the generic iframe embed splits its resize listener out of the element —
            which silently does nothing in a GHL Custom Code block. */}
        <div className="v2-embed-block">
          <div className="v2-embed-head">
            <span className="v2-embed-label">GoHighLevel <em>Funnel &amp; website builder</em></span>
            <Button style={{ marginLeft: "auto" }} onClick={() => copy(ghlEmbed, "ghl")} disabled={!generatedKey}>
              <TbCopy size={15} /> {copied === "ghl" ? "Copied" : "Copy"}
            </Button>
          </div>
          <ol className="v2-embed-steps">
            <li>Open the funnel or website page in the GoHighLevel builder.</li>
            <li>Add Element → <strong>Custom JS/HTML</strong>, and drop it where the scanner should sit.</li>
            <li>Open the element's code editor, paste the block below, and save.</li>
            <li>Publish the page. The scanner does not render in the builder preview — the
                iframe only loads on the published page.</li>
          </ol>
          <pre className="v2-embed-code">{ghlEmbed}</pre>
          <p className="v2-hint" style={{ margin: ".5rem 0 0" }}>
            Leads captured here reach GoHighLevel as contacts when the integration is
            switched on under Settings → Integrations, with the report link on the
            contact. Nothing else is needed to connect the two.
          </p>
        </div>

        <div className="v2-embed-block">
          <div className="v2-embed-head">
            <span className="v2-embed-label">Script tag <em>Legacy</em></span>
            <Button style={{ marginLeft: "auto" }} onClick={() => copy(scriptEmbed, "script")} disabled={!generatedKey}>
              <TbCopy size={15} /> {copied === "script" ? "Copied" : "Copy"}
            </Button>
          </div>
          <pre className="v2-embed-code">{scriptEmbed}</pre>
        </div>
      </div>
      )}

      {/* Withheld until the key exists: every line of it -- the URL, the snippet, the
          Copy button -- is about a key, and a panel explaining how to call an API with
          a key you do not have yet is noise on an already long page. */}
      {generatedKey && (
      <div className="v2-embed">
        <div className="v2-embed-head">
          <span className="v2-section-title v2-section-title--icon"><TbCpu size={15} /> API customization</span>
          <span className="v2-embed-sub">
            For full control and custom integrations, call the scanner directly with your widget key.
          </span>
        </div>

        <div className="v2-embed-block">
          <div className="v2-embed-head">
            <span className="v2-embed-label v2-embed-label--icon"><TbBolt size={14} /> Trigger external risk scan</span>
            <Button style={{ marginLeft: "auto" }} onClick={() => copy(apiExample, "api")}>
              <TbCopy size={15} /> {copied === "api" ? "Copied" : "Copy"}
            </Button>
          </div>
          <pre className="v2-embed-code">{apiExample}</pre>

          <div className="v2-apidoc">
            <div className="v2-apidoc-title">Parameters</div>
            <ul className="v2-apidoc-list">
              <li><code>widgetKey</code> (required): Your widget key</li>
              <li><code>target</code> (required): Domain to scan</li>
              <li>
                <code>modules</code> (optional): overrides what this widget runs. Leave it
                out and the call runs the widget's own report type
                (<code>{leadMagnetType(cfg.reportType).modules.join(", ")}</code>).
              </li>
            </ul>
          </div>
        </div>

        <div className="v2-embed-block">
          <div className="v2-embed-head">
            <span className="v2-embed-label v2-embed-label--icon"><TbShieldLock size={14} /> Before you ship this</span>
          </div>
          <div className="v2-apidoc">
            <ul className="v2-apidoc-list">
              <li>
                The widget key is <strong>not a secret</strong> — it ships in the embed code on
                every page that runs the widget. Treat it as public, and never pair it with a
                credential you would not publish.
              </li>
              <li>
                It is still a spend control. Anyone holding it can run scans against your
                daily limit, so put the call behind your own server or rate limit if the page
                is unauthenticated.
              </li>
              <li>Always call over HTTPS.</li>
              <li>Validate the domain before sending it; the API rejects malformed input, but a round trip is wasted either way.</li>
              <li>Scan results are encrypted before they are stored in S3.</li>
            </ul>
          </div>
        </div>
      </div>
      )}


      {confirm && (
        <ConfirmModal title="Delete this widget?"
          body="The embed stops working immediately on any site using it. Leads already captured are kept."
          onClose={() => setConfirm(null)}
          onConfirm={async () => { await remove(confirm.widgetKey); setConfirm(null); }} />
      )}
    </WebsiteColorsProvider>
  );
};

// Where the leads this widget captures actually go.
//
// The connectors live under Settings -> Integrations, two screens away, and nothing here
// used to mention them — so a tenant could embed a widget, collect leads and never learn
// that none of them reached a CRM. Captures are always stored in Pipsqueak and shown on
// the Leads tab; this panel is only about what happens *beyond* that.
//
// It states what is true rather than nagging: everything connected reads as confirmation,
// and only a workspace with nothing live gets a prompt.
const LeadDelivery = ({ webhookUrl }) => {
  const { destinations, live, dormant, loading, anyLive } = useLeadDestinations();
  const hasWebhook = !!(webhookUrl || "").trim();

  if (loading) return null;

  return (
    <div className="v2-embed">
      <div className="v2-embed-head">
        <span className="v2-section-title v2-section-title--icon">
          <TbPlugConnected size={15} /> Lead delivery
        </span>
        <span className="v2-embed-sub">
          Every lead is saved here and listed on the Leads tab. These send it on.
        </span>
      </div>

      {!anyLive && !hasWebhook && (
        <Alert tone="warning">
          No CRM is connected. Leads this widget captures are stored in Pipsqueak and
          nothing else — set up a destination under{" "}
          <a href="/settings/integrations">Settings → Integrations</a> to have them
          reach GoHighLevel, HubSpot or Zoho automatically.
        </Alert>
      )}

      <div className="v2-destlist">
        {destinations.map((d) => {
          const on = d.configured && d.enabled;
          return (
            <div key={d.id} className={`v2-dest${on ? " on" : ""}`}>
              <span className="v2-dest-icon">
                {on ? <TbCheck size={14} /> : <TbAlertTriangle size={14} />}
              </span>
              <span className="v2-dest-name">{d.name}</span>
              <span className="v2-dest-state">
                {on ? "Connected"
                   : d.configured ? "Configured, switched off"
                   : "Not set up"}
              </span>
              {!on && (
                <a className="v2-dest-link" href="/settings/integrations">
                  {d.configured ? "Review" : "Set up"}
                </a>
              )}
            </div>
          );
        })}
      </div>

      {dormant.length > 0 && (
        <p className="v2-hint" style={{ margin: ".5rem 0 0" }}>
          {dormant.map((d) => d.name).join(" and ")}{" "}
          {dormant.length === 1 ? "is" : "are"} configured but switched off, so{" "}
          {dormant.length === 1 ? "it receives" : "they receive"} nothing until re-enabled.
        </p>
      )}
    </div>
  );
};

const LeadsView = ({ leads }) => {
  const [search, setSearch] = useState("");
  const [lowOnly, setLowOnly] = useState(false);

  const filtered = useMemo(() => {
    let out = leads;
    const q = search.trim().toLowerCase();
    if (q) out = out.filter((l) => ["domain", "username", "email", "phone"].some((k) => (l[k] || "").toLowerCase().includes(q)));
    if (lowOnly) out = out.filter((l) => Number(l.score) < 70);
    return out;
  }, [leads, search, lowOnly]);

  const { sort, toggleSort, sorted } = useSort(filtered, "created_at", ["created_at"]);

  // Derived rather than fetched: there is no stats endpoint, and these are cheap to
  // compute from the rows already on screen.
  const stats = useMemo(() => {
    const scores = leads.map((l) => Number(l.score)).filter((n) => Number.isFinite(n));
    return {
      total: leads.length,
      domains: new Set(leads.map((l) => l.domain).filter(Boolean)).size,
      emails: new Set(leads.map((l) => l.email).filter(Boolean)).size,
      mean: scores.length ? (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(1) : "—",
    };
  }, [leads]);

  const scoreTone = (n) => (n < 50 ? "danger" : n < 70 ? "warning" : "");

  return (
    <>
      <div className="v2-kpis">
        <div className="v2-kpi"><div className="v2-kpi-label">Total scans</div><div className="v2-kpi-value">{stats.total}</div></div>
        <div className="v2-kpi"><div className="v2-kpi-label">Unique domains</div><div className="v2-kpi-value">{stats.domains}</div></div>
        <div className="v2-kpi"><div className="v2-kpi-label">Unique emails</div><div className="v2-kpi-value">{stats.emails}</div></div>
        <div className="v2-kpi"><div className="v2-kpi-label">Mean score</div><div className="v2-kpi-value">{stats.mean}</div></div>
      </div>

      <div className="v2-filters">
        <input className="v2-input" style={{ maxWidth: "18rem" }} type="search" value={search}
               onChange={(e) => setSearch(e.target.value)} placeholder="Search domain, name, email or phone" />
        <button type="button" className={`v2-chip v2-chip-btn${lowOnly ? " on" : ""}`} onClick={() => setLowOnly((v) => !v)}>
          Score <b>&lt; 70</b>
        </button>
        <span className="v2-mono v2-dim" style={{ marginLeft: "auto", fontSize: ".6875rem" }}>
          Showing {sorted.length} of {leads.length}
        </span>
      </div>

      <div className="v2-tablewrap" style={{ border: 0, borderRadius: 0 }}>
        <table className="v2-table">
          <thead>
            <tr>
              <th style={{ width: 44 }}>#</th>
              <SortHeader label="Domain" colKey="domain" sort={sort} onSort={toggleSort} />
              <SortHeader label="Score" colKey="score" sort={sort} onSort={toggleSort} className="v2-num" />
              <SortHeader label="Name" colKey="username" sort={sort} onSort={toggleSort} />
              <SortHeader label="Email" colKey="email" sort={sort} onSort={toggleSort} />
              <SortHeader label="Phone" colKey="phone" sort={sort} onSort={toggleSort} />
              <SortHeader label="Captured" colKey="created_at" sort={sort} onSort={toggleSort} />
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 ? (
              <tr><td colSpan={7}><EmptyState title="No leads yet">Leads appear here once someone runs a scan through an embedded widget.</EmptyState></td></tr>
            ) : sorted.map((l, i) => (
              <tr key={l.id || i}>
                <td className="v2-num v2-dim">{sorted.length - i}</td>
                <td className="v2-mono">{l.domain || "—"}</td>
                <td className={`v2-num ${scoreTone(Number(l.score))}`}>{l.score ?? "—"}</td>
                <td>{l.username || "—"}</td>
                <td className="v2-dim v2-mono">{l.email || "—"}</td>
                <td className="v2-dim v2-mono">{l.phone || "—"}</td>
                <td className="v2-dim">{fmtDateTime(l.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default LeadMagnetScreen;
